/* eslint-disable */
import { useState } from "react";
import { docsApi, iconFor, CATEGORY_COLORS } from "./api";

// Icon choices offered to admins when creating a category. Names map to
// lucide components via iconFor() and are stored on the category row so
// web + mobile render the same glyph.
const ICON_CHOICES = [
  "Folder",
  "FileText",
  "Scale",
  "Gavel",
  "Receipt",
  "FileSignature",
  "BarChart3",
  "ClipboardList",
  "Wrench",
  "Waves",
  "Megaphone",
  "CalendarDays",
  "ShieldCheck",
  "Users",
  "Home",
];

// When `editCat` is provided the modal switches to edit mode: pre-fills
// from the existing category and calls updateCategory instead of createCategory.
export default function CategoryModal({ open, onClose, onCreated, editCat }) {
  const isEdit = !!editCat;
  const [label, setLabel] = useState(editCat?.label ?? "");
  const [color, setColor] = useState(editCat?.color ?? CATEGORY_COLORS[0]);
  const [icon, setIcon] = useState(editCat?.icon ?? "Folder");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Re-seed fields when editCat changes (user picks a different category to edit).
  const prevEditId = useState(editCat?.id)[0];
  if (editCat && editCat.id !== prevEditId) {
    setLabel(editCat.label ?? "");
    setColor(editCat.color ?? CATEGORY_COLORS[0]);
    setIcon(editCat.icon ?? "Folder");
  }

  if (!open) return null;

  const close = () => {
    setLabel(editCat?.label ?? "");
    setColor(editCat?.color ?? CATEGORY_COLORS[0]);
    setIcon(editCat?.icon ?? "Folder");
    setError("");
    setBusy(false);
    onClose();
  };

  const submit = async () => {
    setError("");
    if (!label.trim()) return setError("Name is required");
    setBusy(true);
    try {
      if (isEdit) {
        const updated = await docsApi.updateCategory(editCat.id, {
          label: label.trim(),
          color,
          icon,
        });
        onCreated && onCreated(updated);
      } else {
        const created = await docsApi.createCategory({
          label: label.trim(),
          color,
          icon,
        });
        onCreated && onCreated(created);
      }
      close();
    } catch (e) {
      setError(e.message || (isEdit ? "Could not update category" : "Could not create category"));
      setBusy(false);
    }
  };

  const PreviewIcon = iconFor(icon);

  return (
    <div className="docs-modal-overlay" onClick={close}>
      <div
        className="docs-modal docs-modal-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="docs-modal-header">
          <h2>{isEdit ? "Edit Category" : "New Category"}</h2>
          <button className="docs-modal-close" onClick={close}>
            ×
          </button>
        </div>

        <div className="docs-modal-body">
          <div className="docs-cat-preview">
            <span
              className="docs-cat-icon"
              style={{ background: `${color}1f`, color }}
            >
              <PreviewIcon size={22} strokeWidth={1.8} />
            </span>
            <strong>{label.trim() || "Category name"}</strong>
          </div>

          <div className="docs-field">
            <label>
              Name <span className="req">*</span>
            </label>
            <input
              type="text"
              value={label}
              autoFocus
              maxLength={80}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="e.g. Vendor Contracts"
            />
          </div>

          <div className="docs-field">
            <label>Color</label>
            <div className="docs-swatch-row">
              {CATEGORY_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`docs-swatch ${color === c ? "selected" : ""}`}
                  style={{ background: c }}
                  aria-label={c}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>

          <div className="docs-field">
            <label>Icon</label>
            <div className="docs-icon-grid">
              {ICON_CHOICES.map((name) => {
                const Ic = iconFor(name);
                return (
                  <button
                    key={name}
                    type="button"
                    className={`docs-icon-pick ${icon === name ? "selected" : ""}`}
                    style={icon === name ? { borderColor: color, color } : null}
                    onClick={() => setIcon(name)}
                    aria-label={name}
                  >
                    <Ic size={18} strokeWidth={1.8} />
                  </button>
                );
              })}
            </div>
          </div>

          {error && <div className="docs-error">{error}</div>}
        </div>

        <div className="docs-modal-actions">
          <button className="docs-btn-secondary" onClick={close}>
            Cancel
          </button>
          <button
            className="docs-btn-primary docs-btn-auto"
            disabled={busy || !label.trim()}
            onClick={submit}
          >
            {busy ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save Changes" : "Create Category")}
          </button>
        </div>
      </div>
    </div>
  );
}
